// src/components/Footer/Footer.js

import { useState } from "react";
import {
  FaEnvelope,
  FaPhone,
  FaFacebook,
  FaInstagram,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";
import "./Footer.css";

/*
  EmailJS Configuration

  These values are injected via environment variables and used to
  authenticate and route messages through the EmailJS service.
*/
const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

/*
  FooterLink Component

  Purpose:
  Provides navigation behavior similar to a React Router Link while
  forcing a navigation event even when the user clicks the current route.

  This ensures scroll-to-top behavior and page reinitialization when
  navigating to the same path.
*/
function FooterLink({ to, className, children }) {
  const navigate = useNavigate();

  const handleClick = (e) => {
    e.preventDefault();

    navigate(to, {
      replace: true,
      state: {
        force: Date.now(),
      },
    });
  };

  return (
    <a href={to} className={className} onClick={handleClick}>
      {children}
    </a>
  );
}

/*
  Footer Component

  Responsibilities:
  - Displays contact information and social links
  - Renders a contact form powered by EmailJS
  - Handles spam prevention, validation, and submission feedback
  - Provides site wide footer navigation
*/
export default function Footer() {
  /*
    Static Contact Information

    Centralized configuration for email, phone, and social profiles.
  */
  const contact = {
    email: "lashwithbrittany@gmail.com",
    phone: "5853312162",
    instagram: "https://www.instagram.com/lashwithbrittany/",
    facebook: "https://www.facebook.com/lashwithbrittany",
  };

  /*
    Form State

    website field is an intentional honeypot used for bot detection.
  */
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    website: "",
  });

  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  /*
    Generic Input Handler

    Updates form state using the input name as the key.
  */
  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  /*
    Form Submission Handler

    Safeguards:
    - Honeypot spam detection
    - Required field validation
    - Submission locking while sending

    Enhancements:
    - Captures client IP address
    - Includes page URL and timestamp
  */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (form.website) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;

    setSending(true);

    try {
      const ipResponse = await fetch("https://api.ipify.org?format=json");
      const { ip } = await ipResponse.json();

      const templateParams = {
        sender_name: form.name.trim(),
        sender_email: form.email.trim(),
        sender_phone: form.phone.trim(),
        message: form.message,
        subject: `New message from ${form.name}`,
        email: contact.email,
        submitted_at: new Date().toLocaleString(),
        page_url: window.location.href,
        ip_address: ip || "Unavailable",
      };

      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY
      );

      setSent(true);
      setForm({
        name: "",
        email: "",
        phone: "",
        message: "",
        website: "",
      });

      setTimeout(() => setSent(false), 3500);
    } catch (err) {
      console.error("EmailJS error:", err);
      alert("Sorry, your message could not be sent. Please try again later.");
    } finally {
      setSending(false);
    }
  };

  /*
    Phone Formatter

    Converts a numeric 10 digit phone string into a human readable format.
  */
  const formatPhone = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    return cleaned.length === 10
      ? `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`
      : phone;
  };

  return (
    <footer className="footer-section">
      <div className="footer-connect">
        <div className="footer-header">
          <FaEnvelope className="footer-main-icon" />
          <h2>Get in Touch</h2>
        </div>

        <p className="footer-lead">
          Have a question about services, booking, or availability?
          <br />
          Send a message below and I’ll get back to you as soon as possible.
        </p>

        <div className="footer-contact-card">
          <form className="footer-form" onSubmit={handleSubmit}>
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={handleChange}
              style={{ display: "none" }}
              tabIndex={-1}
              autoComplete="off"
            />

            <input
              type="text"
              name="name"
              placeholder="Your Name"
              value={form.name}
              onChange={handleChange}
              required
            />

            <input
              type="email"
              name="email"
              placeholder="Your Email"
              value={form.email}
              onChange={handleChange}
              required
            />

            <input
              type="tel"
              name="phone"
              placeholder="Your Phone (optional)"
              value={form.phone}
              onChange={handleChange}
            />

            <textarea
              name="message"
              placeholder="Your Message"
              rows={3}
              value={form.message}
              onChange={handleChange}
              required
            />

            <button type="submit" disabled={sending}>
              {sending ? "Sending..." : sent ? "Message Sent" : "Send Message"}
            </button>

            {sent && (
              <p className="sent-message" role="status">
                Thank you for reaching out. I’ll be in touch soon.
              </p>
            )}
          </form>

          <div className="footer-contact-info">
            <div>
              <FaEnvelope className="footer-icon" />
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
            </div>

            <div>
              <FaPhone className="footer-icon" />
              <a href={`tel:${contact.phone}`}>
                {formatPhone(contact.phone)}
              </a>
            </div>

            <div className="footer-social">
              <a
                href={contact.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
              >
                <FaInstagram />
              </a>

              <a
                href={contact.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
              >
                <FaFacebook />
              </a>
            </div>

            <a href={`tel:${contact.phone}`} className="footer-call-cta">
              <FaPhone /> Call Now
            </a>

            <FooterLink to="/book" className="footer-book-cta">
              Schedule Your Visit
            </FooterLink>
          </div>
        </div>
      </div>

      <div className="footer-links-bar">
        <FooterLink to="/">Home</FooterLink>
        <FooterLink to="/pricelist">Services</FooterLink>
        <FooterLink to="/gallery">Gallery</FooterLink>
        <FooterLink to="/reviews">Reviews</FooterLink>
        <FooterLink to="/permanent-jewelry">Permanent Jewelry</FooterLink>
        <FooterLink to="/policy">Policy</FooterLink>
        <FooterLink to="/privacy">Privacy</FooterLink>
        <FooterLink to="/contact-me">Contact</FooterLink>
        <FooterLink to="/giftcards">Gift Cards</FooterLink>
        <FooterLink to="/book">Book Now</FooterLink>
      </div>

      <div className="footer-bottom">
        © {new Date().getFullYear()} Lash With Brittany. All rights reserved.
      </div>
    </footer>
  );
}
